import { useRef, useState } from 'react';
import type { CSSProperties, DragEvent } from 'react';
import { formatFileSize } from '../chat/useChatAttachments';
import { Icon } from '../ui';
import { ATTACHMENT_ACCEPT, ATTACHMENT_HINT, type AiJobAttachment } from '../../lib/aiJobAttachments';
import { glyphFor, type PendingAttachment } from './attachmentPicker';

/**
 * Dropzone + rows for files picked with useAttachmentPicker, and the read-only
 * list of a job's uploaded attachments.
 */

/* ---------- rows ---------- */

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '7px 10px',
  margin: '0 -10px',
  borderRadius: 'var(--radius-sm)',
};

const nameStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 'var(--text-sm)',
  color: 'var(--text-body)',
};

const sizeStyle: CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--text-faint)',
  flexShrink: 0,
};

function FileRow({ item, onRemove, disabled }: { item: PendingAttachment; onRemove: () => void; disabled: boolean }) {
  const Glyph = glyphFor(item.file.name, item.file.type);
  return (
    <div className="ck-check-row hover-wash" style={rowStyle}>
      <Glyph size={16} strokeWidth={1.75} style={{ color: 'var(--text-faint)', flexShrink: 0 }} aria-hidden="true" />
      <span title={item.file.name} style={nameStyle}>
        {item.file.name}
      </span>
      <span className="t-num" style={sizeStyle}>
        {formatFileSize(item.file.size)}
      </span>
      {disabled ? null : (
        <button
          type="button"
          className="pressable ck-check-remove"
          aria-label={`Remove ${item.file.name}`}
          onClick={onRemove}
          style={{
            display: 'inline-flex',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-faint)',
            cursor: 'pointer',
            padding: '3px 5px',
          }}
        >
          <Icon name="x" size={12} />
        </button>
      )}
    </div>
  );
}

/** Uploaded attachments on an existing job — name links to the public URL. */
export function AttachmentLinks({ attachments }: { attachments: AiJobAttachment[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {attachments.map((att) => {
        const Glyph = glyphFor(att.name, att.type);
        return (
          <a
            key={att.url}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            title={att.name}
            className="hover-wash"
            style={{ ...rowStyle, textDecoration: 'none' }}
          >
            <Glyph size={16} strokeWidth={1.75} style={{ color: 'var(--text-faint)', flexShrink: 0 }} aria-hidden="true" />
            <span style={{ ...nameStyle, color: 'var(--accent-text)' }}>{att.name}</span>
            <span className="t-num" style={sizeStyle}>
              {formatFileSize(att.size)}
            </span>
            <Icon name="external-link" size={12} style={{ flexShrink: 0, color: 'var(--text-faint)' }} />
          </a>
        );
      })}
    </div>
  );
}

/* ---------- dropzone ---------- */

interface AttachmentDropzoneProps {
  files: PendingAttachment[];
  error: string | null;
  onAdd: (files: FileList | File[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  /** Tighter padding for side panels. */
  compact?: boolean;
  hint?: string;
}

export default function AttachmentDropzone({
  files,
  error,
  onAdd,
  onRemove,
  disabled = false,
  compact = false,
  hint = ATTACHMENT_HINT,
}: AttachmentDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    if (e.dataTransfer.types.includes('Files')) setDragging(true);
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;
    if (e.dataTransfer.files.length > 0) onAdd(e.dataTransfer.files);
  };

  const iconSize = compact ? 28 : 34;

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className={disabled ? '' : 'pressable'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? '4px' : '6px',
          padding: compact ? '14px 12px' : '22px 16px',
          textAlign: 'center',
          background: dragging ? 'var(--accent-soft)' : 'var(--surface-input)',
          border: `1px dashed ${dragging ? 'var(--border-focus)' : 'var(--border-strong)'}`,
          borderRadius: 'var(--radius-md)',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          outline: 'none',
          transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${iconSize}px`,
            height: `${iconSize}px`,
            borderRadius: 'var(--radius-pill)',
            background: dragging ? 'var(--accent-soft-hover)' : 'var(--surface-raised)',
            border: '1px solid var(--border-hairline)',
            color: dragging ? 'var(--accent-text)' : 'var(--text-muted)',
            transition: 'background var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
          }}
        >
          <Icon name="paperclip" size={compact ? 13 : 15} />
        </span>
        <span
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)' as CSSProperties['fontWeight'],
            color: dragging ? 'var(--accent-text)' : 'var(--text-body)',
          }}
        >
          {dragging ? 'Drop to attach' : 'Drop files here, or browse'}
        </span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{hint}</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          hidden
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) onAdd(e.target.files);
            e.target.value = ''; // so the same file can be picked again
          }}
        />
      </div>

      {error ? (
        <div
          style={{
            marginTop: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: 'var(--text-xs)',
            color: 'var(--danger-400)',
          }}
        >
          <Icon name="circle-alert" size={13} />
          {error}
        </div>
      ) : null}

      {files.length > 0 ? (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {files.map((item) => (
            <FileRow key={item.id} item={item} onRemove={() => onRemove(item.id)} disabled={disabled} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
